import { ListFilter } from "lucide-react";
import * as React from "react";
import { cn } from "../../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";

export interface SurfaceFiltersPopoverProps {
  /** Accessible name for the trigger (required — it's an icon+text button). */
  ariaLabel: string;
  /** Visible trigger label. Defaults to "Filtri". */
  label?: React.ReactNode;
  /** Count of active filters — rendered as an orange badge on the trigger. */
  activeCount?: number;
  /** Popover width in px (default 304). */
  width?: number;
  /** Popover side alignment (default "end"). */
  align?: "start" | "center" | "end";
  /** The filter sections — arbitrary content (chips, sub-tabs, dropdowns). */
  children: React.ReactNode;
}

/**
 * SurfaceFiltersPopover — the shared "Filtri ▾" popover shell every workspace
 * hub hangs its filters off (Jul 2026 command-bar de-layering).
 *
 * DRY: one trigger + badge + popover chrome; each hub passes ONLY its own
 * filter sections as ``children`` (smart-view chips, channel sub-tabs, the
 * per-hub search-filter dropdowns). The command bar stays a single row and the
 * filters live one click away.
 *
 * Pencil aesthetic (per @ando/ui review skill): a hairline-outlined ghost
 * trigger — no fill — with the single orange accent reserved for the active
 * badge. ``space-y-3.5`` groups the sections inside the standard popover.
 */
export const SurfaceFiltersPopover: React.FC<SurfaceFiltersPopoverProps> = ({
  ariaLabel,
  label,
  activeCount = 0,
  width = 304,
  align = "end",
  children,
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        aria-label={ariaLabel}
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-hairline px-2.5 font-medium text-[12px] text-ink-2 transition-colors hover:border-ando-orange/40 hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ListFilter className="h-3.5 w-3.5" aria-hidden />
        {label ?? "Filtri"}
        {activeCount > 0 ? (
          <span className="rounded-full bg-ando-orange px-1.5 font-semibold text-[10px] text-white tabular-nums">
            {activeCount}
          </span>
        ) : null}
      </button>
    </PopoverTrigger>
    <PopoverContent
      align={align}
      style={{ width }}
      className={cn("space-y-3.5")}
    >
      {children}
    </PopoverContent>
  </Popover>
);
SurfaceFiltersPopover.displayName = "SurfaceFiltersPopover";

/**
 * SurfaceFilterSection — a labelled group inside {@link SurfaceFiltersPopover}
 * (tiny uppercase ink-3 eyebrow + its controls). Keeps every hub's popover
 * sections visually identical.
 */
export const SurfaceFilterSection: React.FC<{
  label: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <section>
    <div className="mb-1.5 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.08em]">
      {label}
    </div>
    {children}
  </section>
);
SurfaceFilterSection.displayName = "SurfaceFilterSection";
